// client/src/components/public/ChatWidget.js — the website assistant, on every public page.
//
// Renders nothing unless the server says the assistant is switched on. Talks only to
// this site's own /api/chat (the CSP allows connect-src 'self' and nothing else), keeps
// the visitor's session token in sessionStorage, and never asks for contact details
// except through the consent form below.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const KEY = 'brecChatSession';
const store = {
    get: () => { try { return sessionStorage.getItem(KEY); } catch (e) { return null; } },
    set: (v) => { try { sessionStorage.setItem(KEY, v); } catch (e) { /* private mode */ } },
    clear: () => { try { sessionStorage.removeItem(KEY); } catch (e) { /* private mode */ } },
};

async function call(path, { method = 'GET', body, token } = {}) {
    const res = await fetch(`/api/chat${path}`, {
        method,
        headers: { 'Content-Type': 'application/json', ...(token ? { 'X-Chat-Session': token } : {}) },
        body: body ? JSON.stringify(body) : undefined,
    });
    let data = {};
    try { data = await res.json(); } catch (e) { /* empty */ }
    if (!res.ok) { const err = new Error(data.message || 'Something went wrong.'); err.status = res.status; throw err; }
    return data;
}

const PURPOSE_LABEL = { showing: 'Request a showing', callback: 'Request a call back', question: 'Send us your question',
    selling: 'Tell us about your property', buying: 'Tell us what you are looking for', renting: 'Tell us what you need' };

function ContactForm({ purpose, propertyId, consentText, token, onDone, onCancel }) {
    const [f, setF] = useState({ name: '', email: '', phone: '', preferred_time: '', message: '', website: '' });
    const [consent, setConsent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
    const submit = async (e) => {
        e.preventDefault();
        setErr('');
        if (!f.email && !f.phone) { setErr('Please give an email address or a phone number.'); return; }
        if (!consent) { setErr('Please tick the box so we may contact you.'); return; }
        setBusy(true);
        try {
            const r = await call('/contact', { method: 'POST', token, body: { ...f, purpose, property_id: propertyId || null, consent: true } });
            onDone(r.messages || []);
        } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
    };
    const id = (k) => `chat-cf-${k}`;
    return (
        <form onSubmit={submit} className="mt-2 p-3 rounded-lg border border-indigo-200 bg-indigo-50 space-y-2 text-sm" aria-label={PURPOSE_LABEL[purpose] || 'Contact form'}>
            <div className="font-semibold text-slate-800">{PURPOSE_LABEL[purpose] || 'Contact us'}</div>
            <input type="text" name="website" value={f.website} onChange={set('website')} tabIndex="-1" autoComplete="off" aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }} />
            <label htmlFor={id('name')} className="block text-slate-700">Name
                <input id={id('name')} required value={f.name} onChange={set('name')} autoComplete="name" className="mt-0.5 w-full p-2 border rounded" /></label>
            <label htmlFor={id('email')} className="block text-slate-700">Email
                <input id={id('email')} type="email" value={f.email} onChange={set('email')} autoComplete="email" className="mt-0.5 w-full p-2 border rounded" /></label>
            <label htmlFor={id('phone')} className="block text-slate-700">Phone
                <input id={id('phone')} type="tel" value={f.phone} onChange={set('phone')} autoComplete="tel" className="mt-0.5 w-full p-2 border rounded" /></label>
            {(purpose === 'showing' || purpose === 'callback') && (
                <label htmlFor={id('time')} className="block text-slate-700">Best time (optional)
                    <input id={id('time')} value={f.preferred_time} onChange={set('preferred_time')} maxLength={120} className="mt-0.5 w-full p-2 border rounded" /></label>
            )}
            <label htmlFor={id('msg')} className="block text-slate-700">Anything else (optional)
                <textarea id={id('msg')} rows="2" value={f.message} onChange={set('message')} maxLength={1000} className="mt-0.5 w-full p-2 border rounded" /></label>
            <label className="flex items-start gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" required />
                <span>{consentText}</span>
            </label>
            {err && <p role="alert" className="text-red-600 text-xs">{err}</p>}
            <div className="flex gap-2">
                <button type="submit" disabled={busy} className="px-3 py-1.5 rounded bg-slate-800 text-white disabled:bg-indigo-300">{busy ? 'Sending…' : 'Send'}</button>
                <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded border border-slate-300 text-slate-700">Cancel</button>
            </div>
        </form>
    );
}

function Message({ m }) {
    const mine = m.sender === 'guest';
    const who = m.sender === 'staff' ? 'Team member' : m.sender === 'guest' ? 'You' : m.sender === 'system' ? null : 'AI assistant';
    return (
        <li className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-line ${mine ? 'bg-slate-800 text-white'
                : m.sender === 'staff' ? 'bg-emerald-50 border border-emerald-200 text-slate-800'
                : m.sender === 'system' ? 'bg-slate-50 text-slate-500 italic' : 'bg-slate-100 text-slate-800'}`}>
                {who && <span className="sr-only">{who}: </span>}
                {m.sender === 'staff' && <div className="text-xs font-semibold text-emerald-700 mb-0.5">Team member</div>}
                {m.body}
                {Array.isArray(m.citations) && m.citations.length > 0 && (
                    <div className="mt-1 text-xs text-slate-500">Source: {m.citations.map((c, i) => (
                        <span key={`${c.kind}-${c.ref}`}>{i > 0 && ', '}<a href={c.url} className="underline hover:text-indigo-700">{c.label}</a></span>
                    ))}</div>
                )}
            </div>
        </li>
    );
}

const ChatWidget = () => {
    const location = useLocation();
    const [config, setConfig] = useState(null);
    const [open, setOpen] = useState(false);
    const [token, setToken] = useState(store.get());
    const [messages, setMessages] = useState([]);
    const [mode, setMode] = useState('bot');
    const [form, setForm] = useState(null);       // { purpose, property_id }
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const lastId = messages.length ? Math.max(...messages.map((m) => Number(m.message_id) || 0)) : 0;

    useEffect(() => {
        let live = true;
        call('/config').then((c) => { if (live) setConfig(c); }).catch(() => { if (live) setConfig({ enabled: false }); });
        return () => { live = false; };
    }, []);

    const append = useCallback((list) => {
        setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.message_id));
            return [...prev, ...list.filter((m) => !m.message_id || !seen.has(m.message_id))];
        });
    }, []);

    const handleActions = useCallback((actions = []) => {
        const f = actions.find((a) => a.type === 'contact_form');
        if (f) setForm({ purpose: f.purpose, property_id: f.property_id });
        if (actions.some((a) => a.type === 'waiting_human')) setMode('waiting_human');
    }, []);

    // Restore an existing session, or start one, when the panel first opens.
    useEffect(() => {
        if (!open || !config?.enabled) return;
        let live = true;
        (async () => {
            try {
                if (token && !messages.length) {
                    const r = await call('/messages?after=0', { token });
                    if (live) { append(r.messages); setMode(r.mode); }
                } else if (!token) {
                    const r = await call('/start', { method: 'POST', body: { page: location.pathname } });
                    if (!live) return;
                    store.set(r.token); setToken(r.token); append(r.messages);
                }
            } catch (e) {
                if (e.status === 404) { store.clear(); setToken(null); setMessages([]); }
                else if (live) setErr(e.message);
            }
        })();
        return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, token, config]);

    // While a person is (or is about to be) on the other end, check for their replies.
    useEffect(() => {
        if (!open || !token || !(mode === 'waiting_human' || mode === 'human')) return undefined;
        const t = setInterval(async () => {
            try { const r = await call(`/messages?after=${lastId}`, { token }); append(r.messages); setMode(r.mode); } catch (e) { /* next tick */ }
        }, 4000);
        return () => clearInterval(t);
    }, [open, token, mode, lastId, append]);

    useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);
    useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages, form]);

    if (!config || !config.enabled) return null;

    const send = async (e) => {
        e.preventDefault();
        const t = text.trim();
        if (!t || busy || !token) return;
        setBusy(true); setErr('');
        append([{ message_id: `local-${Date.now()}`, sender: 'guest', body: t }]);
        setText('');
        try {
            const r = await call('/message', { method: 'POST', token, body: { text: t } });
            append(r.messages || []); setMode(r.mode || mode); handleActions(r.actions);
        } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
    };
    const askPerson = async () => {
        if (!token) return;
        setBusy(true); setErr('');
        try { const r = await call('/handoff', { method: 'POST', token }); append(r.messages); handleActions(r.actions); }
        catch (e) { setErr(e.message); } finally { setBusy(false); }
    };
    const endChat = async () => {
        try { if (token) await call('/end', { method: 'POST', token }); } catch (e) { /* ignore */ }
        store.clear(); setToken(null); setMessages([]); setMode('bot'); setForm(null); setOpen(false);
    };

    return (
        <>
            {!open && (
                <button type="button" onClick={() => setOpen(true)} aria-haspopup="dialog"
                    className="fixed bottom-4 right-4 z-50 rounded-full bg-slate-800 text-white shadow-lg px-5 py-3 text-sm font-medium hover:bg-slate-900 focus:outline-none focus:ring-4 focus:ring-indigo-300">
                    Questions? Chat with us
                </button>
            )}
            {open && (
                <section role="dialog" aria-modal="false" aria-labelledby="chat-title"
                    onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
                    className="fixed z-50 inset-x-0 bottom-0 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:w-96 h-[85vh] sm:h-[34rem] bg-white sm:rounded-xl shadow-2xl border border-slate-200 flex flex-col">
                    <header className="px-4 py-3 bg-slate-800 text-white sm:rounded-t-xl flex items-start justify-between gap-2">
                        <div>
                            <h2 id="chat-title" className="text-sm font-semibold">{mode === 'human' ? 'Chatting with our team' : 'AI assistant'}</h2>
                            <p className="text-xs text-slate-300">Automated — not a person. {mode === 'waiting_human' ? 'Waiting for a team member…' : ''}</p>
                        </div>
                        <div className="flex gap-1">
                            <button type="button" onClick={() => setOpen(false)} className="text-slate-300 hover:text-white px-2" aria-label="Minimise chat">–</button>
                            <button type="button" onClick={endChat} className="text-slate-300 hover:text-white px-2" aria-label="End chat">×</button>
                        </div>
                    </header>
                    <ul ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2" aria-live="polite" aria-label="Conversation">
                        {messages.map((m) => <Message key={m.message_id} m={m} />)}
                        {form && (
                            <li><ContactForm purpose={form.purpose} propertyId={form.property_id} consentText={config.consent_text} token={token}
                                onDone={(list) => { append(list); setForm(null); }} onCancel={() => setForm(null)} /></li>
                        )}
                    </ul>
                    {err && <p role="alert" className="px-3 text-xs text-red-600">{err}</p>}
                    <div className="px-3 pt-2 flex flex-wrap gap-2 text-xs">
                        {mode === 'bot' && <button type="button" onClick={askPerson} disabled={busy} className="px-2 py-1 rounded-full border border-indigo-300 text-indigo-700 hover:bg-indigo-50">Talk to a person</button>}
                        <a href="/listings" className="px-2 py-1 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-50">Browse listings</a>
                        <button type="button" onClick={() => setForm({ purpose: 'question' })} className="px-2 py-1 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-50">Leave my details</button>
                    </div>
                    <form onSubmit={send} className="p-3 flex gap-2 border-t border-slate-200 mt-2">
                        <label htmlFor="chat-input" className="sr-only">Type your message</label>
                        <input id="chat-input" ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} maxLength={1000}
                            placeholder="Type your question…" autoComplete="off" disabled={!token}
                            className="flex-1 p-2 border border-slate-300 rounded text-sm" />
                        <button type="submit" disabled={busy || !text.trim()} className="px-3 py-2 rounded bg-slate-800 text-white text-sm disabled:bg-indigo-300">Send</button>
                    </form>
                    {config.identity && <p className="px-3 pb-2 text-[11px] text-slate-500">{config.identity}</p>}
                </section>
            )}
        </>
    );
};

export default ChatWidget;
